import React, { Dispatch, SetStateAction, useState } from 'react';
import { Modal, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from '../../../components/MyAppText';
import { translate } from '../../../services/translate';
import FormActions from '../../../components/FormActions';
import { WindowInformation } from '../../../services/window-information';
import Toast from 'react-native-toast-message';
import { CannotDeleteVacancy, deacService, VacancyCancellationReasonsType } from '../../../services/central-api/deac';
import CancellationReasonsDropdown from '../../../components/deac/CancellationReasonsDropdown';

interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    vacancy: Vacancy;
    onConfirm: () => void;
}

export default function DeleteVacancyModal({ vacancy, isModalVisible, setModalVisible, onConfirm }: ModalProps) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [cancellationReasonId, setCancellationReasonId] = useState<number>(0);

    async function formSubmit() {
        try {
            setIsLoading(true);

            await deacService.deleteVacancy(vacancy.id, cancellationReasonId);

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            onConfirm();
        } catch (err) {
            if (err instanceof CannotDeleteVacancy) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate('cannotDeleteVacancy'),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsLoading(false);
            closeModal();
        }
    }

    const closeModal = () => {
        setCancellationReasonId(0);
        setModalVisible(false);
    };

    return (
        <Modal
            transparent
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => closeModal()}
        >
            <View style={styles.container}>
                <View style={styles.formContainer(windowInfo.isMobile)}>
                    <View style={{ flexDirection: 'column', rowGap: 8 }}>
                        <MyAppText style={styles.header}>{translate('vacancyCancellationReason')}</MyAppText>
                        <MyAppText style={styles.subheader}>{translate('provideReasonBelow')}</MyAppText>
                    </View>
                    <CancellationReasonsDropdown
                        setValue={setCancellationReasonId}
                        value={cancellationReasonId}
                        purpose={VacancyCancellationReasonsType.VACANCY_WEB_PANEL}
                        zIndex={2}
                        labelStyle={styles.reasonLabelStyle}
                        viewStyle={{ gap: 8 }}
                    />
                    <FormActions
                        viewStyle={{ flex: 1, justifyContent: 'space-around' }}
                        buttonStyle={{ paddingHorizontal: 16, paddingVertical: 9, }}
                        buttonContentContainerStyle={{ flex: 1 }}
                        cancelText='goBack'
                        confirmText='cancelVacancy'
                        onSubmit={formSubmit}
                        onClose={closeModal}
                        disabled={isLoading || cancellationReasonId == 0}
                        isLoading={isLoading}
                    />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1,
    },
    formContainer: (isMobile: boolean) => ({
        width: isMobile ? '95%' : 450,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        rowGap: 32,
        padding: 32
    }),
    header: {
        fontSize: 18,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
    },
    subheader: {
        fontSize: 16,
        fontWeight: '400',
        flexWrap: 'wrap',
        color: theme.colors.modal.subheader,
    },
    reasonLabelStyle: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 14,
    }
}));
